/**
 * Contract Comparison Matrix — side-by-side compare N contracts on value / risk / CPI / SPI / LD-cap / warranty.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, Plus, X, Search, Scale } from 'lucide-react';
import api from '@/lib/api';

const fmtINR = (v) => v == null ? '—' : (Math.abs(v) >= 1e7 ? `₹${(v / 1e7).toFixed(2)} Cr` : Math.abs(v) >= 1e5 ? `₹${(v / 1e5).toFixed(2)} L` : `₹${Number(v).toLocaleString('en-IN')}`);
const riskColor = (s) => s == null ? '#94A3B8' : s <= 30 ? '#22C55E' : s <= 60 ? '#F59E0B' : '#EF4444';

export default function ContractsCompare() {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => { (async () => {
    try { const r = await api.get('/contracts/portfolio'); setPortfolio(r.data.contracts || []); }
    catch { toast.error('Failed to load contracts'); } finally { setLoading(false); }
  })(); }, []);

  const filtered = useMemo(() => portfolio.filter(c => {
    const t = q.toLowerCase();
    return !t || (c.title || '').toLowerCase().includes(t) || (c.contract_number || '').toLowerCase().includes(t) || (c.project || '').toLowerCase().includes(t);
  }), [portfolio, q]);

  const toggle = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : (prev.length >= 6 ? (toast.error('Max 6 contracts to compare'), prev) : [...prev, id]));
  };

  const runCompare = async () => {
    if (selectedIds.length < 2) return toast.error('Pick at least 2 contracts');
    setComparing(true);
    try { const r = await api.get(`/contracts/compare?ids=${selectedIds.join(',')}`); setCompareData(r.data); toast.success(`Comparing ${r.data.count} contracts`); }
    catch { toast.error('Compare failed'); } finally { setComparing(false); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div></DashboardLayout>;

  const rows = compareData?.contracts || [];

  // Best/worst indicators per numeric field
  const bestBy = (field, higherIsBetter = false) => {
    if (rows.length === 0) return null;
    const vals = rows.map(r => r[field]).filter(v => v != null);
    if (!vals.length) return null;
    return higherIsBetter ? Math.max(...vals) : Math.min(...vals);
  };
  const winners = {
    value: bestBy('value', true), risk_score: bestBy('risk_score', false), cpi: bestBy('cpi', true), spi: bestBy('spi', true),
    ld_cap_pct: bestBy('ld_cap_pct', true), retention_pct: bestBy('retention_pct', false),
    claims_exposure: bestBy('claims_exposure', false), ld_exposure: bestBy('ld_exposure', false),
  };
  const badge = (val, best, fmt = (v) => v) => val == null ? <span className="text-muted-foreground">—</span> : (
    <span style={{ color: val === best ? '#22C55E' : '#E2E8F0', fontWeight: val === best ? 600 : 400 }}>{fmt(val)}{val === best && ' ✓'}</span>
  );

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="contracts-compare">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')} data-testid="cmp-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Contract Comparison Matrix</h1>
            <p className="text-sm text-muted-foreground mt-1">Side-by-side compare up to 6 contracts on value · risk · CPI · SPI · LD-cap · warranty</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4">
          {/* Selector pane */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search contract..." className="pl-10 bg-muted border-border text-foreground h-8" data-testid="cmp-search" />
                </div>
              </div>
              <div className="max-h-[520px] overflow-y-auto space-y-1 mb-3" data-testid="cmp-selector">
                {filtered.slice(0, 200).map(c => {
                  const sel = selectedIds.includes(c.id);
                  return (
                    <div key={c.id} onClick={() => toggle(c.id)} className={`p-2 rounded cursor-pointer flex items-center gap-2 border ${sel ? 'bg-purple-500/10 border-purple-500/50' : 'border-transparent hover:bg-white/[0.02]'}`} data-testid={`cmp-select-${c.id}`}>
                      {sel ? <X className="w-3.5 h-3.5 text-purple-400 shrink-0" /> : <Plus className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-foreground truncate">{c.title || 'Untitled'}</p>
                        <p className="text-[10px] text-muted-foreground truncate">{c.project} · {fmtINR(c.value)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button onClick={runCompare} disabled={selectedIds.length < 2 || comparing} className="w-full bg-purple-600 hover:bg-purple-700 text-foreground" data-testid="cmp-run-btn">
                {comparing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Scale className="w-3.5 h-3.5 mr-1" />}Compare {selectedIds.length || ''}
              </Button>
              <p className="text-[10px] text-muted-foreground mt-2 text-center">{selectedIds.length} selected · max 6</p>
            </CardContent>
          </Card>

          {/* Matrix pane */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              {rows.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
                  <Scale className="w-10 h-10 mb-3" />
                  <p className="text-sm">Pick 2-6 contracts and click Compare to see the side-by-side matrix</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="cmp-matrix">
                    <thead>
                      <tr className="text-muted-foreground uppercase text-[10px]">
                        <th className="text-left px-2 py-2 sticky left-0 bg-card z-10">Metric</th>
                        {rows.map(c => <th key={c.id} className="text-left px-2 py-2 min-w-[180px]">
                          <div className="text-foreground text-xs normal-case font-semibold">{c.title || c.contract_number}</div>
                          <div className="text-muted-foreground text-[10px]">{c.contract_number}</div>
                          <Badge variant="outline" className="text-[9px] mt-1">{c.status}</Badge>
                        </th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { label: 'Type', get: c => c.type },
                        { label: 'Value', get: c => badge(c.value, winners.value, fmtINR) },
                        { label: 'Currency', get: c => c.currency },
                        { label: 'Start Date', get: c => c.start_date?.slice(0, 10) },
                        { label: 'End Date', get: c => c.end_date?.slice(0, 10) },
                        { label: 'Risk Score', get: c => c.risk_score == null ? '—' : <Badge style={{ background: `${riskColor(c.risk_score)}22`, color: riskColor(c.risk_score) }} className="text-[10px]">{c.risk_score}{c.risk_score === winners.risk_score && ' ✓'}</Badge> },
                        { label: 'CPI', get: c => badge(c.cpi, winners.cpi, v => v.toFixed(2)) },
                        { label: 'SPI', get: c => badge(c.spi, winners.spi, v => v.toFixed(2)) },
                        { label: 'LD Cap %', get: c => badge(c.ld_cap_pct, winners.ld_cap_pct, v => `${v}%`) },
                        { label: 'LD Exposure', get: c => badge(c.ld_exposure, winners.ld_exposure, fmtINR) },
                        { label: 'LD Cap Used %', get: c => c.ld_cap_used_pct == null ? '—' : `${c.ld_cap_used_pct}%` },
                        { label: 'Retention %', get: c => badge(c.retention_pct, winners.retention_pct, v => `${v}%`) },
                        { label: 'Warranty (mo)', get: c => c.warranty_period || '—' },
                        { label: 'Clauses', get: c => c.clauses_total || '—' },
                        { label: 'Open Claims', get: c => c.claims_open || 0 },
                        { label: 'Claims Exposure', get: c => badge(c.claims_exposure, winners.claims_exposure, fmtINR) },
                        { label: 'Top Risks', get: c => (
                          <div className="flex flex-wrap gap-1">{(c.top_risks || []).slice(0, 3).map((r, i) => <Badge key={i} variant="outline" className="text-[9px]">{typeof r === 'string' ? r : r.title || r.description}</Badge>)}</div>
                        ) },
                      ].map(row => (
                        <tr key={row.label} className="border-t border-border" data-testid={`cmp-row-${row.label.replace(/[^\w]/g, '-')}`}>
                          <td className="px-2 py-2 text-muted-foreground sticky left-0 bg-card z-10">{row.label}</td>
                          {rows.map(c => <td key={c.id} className="px-2 py-2 text-foreground">{row.get(c)}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
